import { TimeOffPoliciesResource } from '~/resources';
import { TIME_OFF_POLICY_ERRORS_TO_CHECK } from '~/app_constants';
import { isEmpty } from 'lodash-es';

const ANNUAL_TYPE = '2';

export default {
  props: {
    organizationId: {
      type: Number,
      required: true,
    },
    orgTimeZone: {
      type: String,
      default: 'UTC',
    },
    dropdownOptions: {
      type: Object,
      required: true,
    },
    countries: {
      type: Array, // [{ id:, text: }]
      required: true,
    },
    monthlyAccrualEnabled: {
      type: Boolean,
      default: false,
    },
    viewMode: {
      type: Boolean,
      default: false,
    }
  },
  data: () => ({
    timeOffPolicy: {},
    showDialog: false,
    saving: false,
    editing: false,
    membersData: {},
    loadingPolicy: false,
    membersAddedBy: 0,
  }),
  created() {
    this.resetTimeOffPolicy();
  },
  computed: {
    hoursWorkedSelected() {
      return Number(this.timeOffPolicy.accrual_type) === 1;
    },
    monthlySelected() {
      return Number(this.timeOffPolicy.accrual_type) === 4;
    },
  },
  methods: {
    openDialog(timeOffPolicyId) {
      if (timeOffPolicyId) {
        this.loadingPolicy = true;
        this.loadTimeOffPolicy(timeOffPolicyId);
        this.editing = !this.viewMode;
      } else {
        this.showDialog = true;
      }
    },
    async loadTimeOffPolicy(id) {
      const action = this.viewMode ? 'show' : 'edit';

      try {
        const response = await TimeOffPoliciesResource[action]({ id });

        // Cast booleans and numbers to strings so that dropdowns understand them
        for (let [key, value] of Object.entries(response)) {
          if ((typeof value === 'boolean' || typeof value === 'number') &&
              key !== 'auto_add_new_users' &&
              !this.monthlyAccrualEnabled
          ) {
            value = value.toString();
          }
          this.timeOffPolicy[key] = value;
        }
        if (this.hoursWorkedSelected) {
          this.timeOffPolicy.accrual_policy.hours_per_year = this.timeOffPolicy.accrual_policy.maximum_to_accrue;
          delete this.timeOffPolicy.accrual_policy.maximum_to_accrue;
        }
        if (this.monthlySelected && this.timeOffPolicy.accrual_policy.accrual_period === 'year') {
          this.$set(this.timeOffPolicy.accrual_policy, 'hours', this.timeOffPolicy.accrual_policy.hours_per_year);
        } else if (this.monthlySelected && this.timeOffPolicy.accrual_policy.accrual_period === 'month') {
          this.$set(this.timeOffPolicy.accrual_policy, 'hours', this.timeOffPolicy.accrual_policy.hours_per_month);
        }
        this.showDialog = true;
      } catch (error) {
        this.$appMessage('Unexpected error. Please try again.');
      } finally {
        this.loadingPolicy = false;
      }
    },
    closeDialogs() {
      this.showDialog = false;
      this.$emit('close');
      this.reloadWizard();
    },
    reloadWizard() {
      if (this.$refs.dialogObserver) {
        this.$refs.dialogObserver.reset();
      }
      this.step = 0;
      this.resetTimeOffPolicy();
      this.saving = false;
      this.editing = false;
      this.loadingPolicy = false;
    },
    resetTimeOffPolicy() {
      if (this.monthlyAccrualEnabled) {
        this.timeOffPolicy = {
          accrual_policy: {},
          accrual_type: ANNUAL_TYPE,
          allow_negative_balances: true,
          auto_add_new_users: false,
          balance_rolls_over_annually: true,
          id: null,
          membership_rules: {},
          name: null,
          paid: true,
          requires_approval: false,
          has_membership_rules: false,
        };
      } else {
        this.timeOffPolicy = {
          accrual_policy: {},
          accrual_type: ANNUAL_TYPE,
          allow_negative_balances: 'false',
          auto_add_new_users: false,
          balance_rolls_over_annually: 'false',
          id: null,
          membership_rules: {},
          name: null,
          paid: 'false',
          requires_approval: 'false',
          has_membership_rules: 'false',
        };
      }
    },
    policyUpdated(field, value) {
      this.timeOffPolicy[field] = value;
    },
    memberOptionsUpdated(value) {
      switch (value) {
        case '2':
          this.membersAddedBy = 'country_and_employee_type';
          break;
        case '3':
          this.membersAddedBy = 'csv';
          break;
        default:
          this.membersAddedBy = 'members_list';
          break;
      }
    },
    async save() {
      if (this.viewMode) { return };

      const isValid = await this.$refs.dialogObserver.validate();

      if (isValid) {
        this.saving = true;

        await this.saveTimeOffPolicy();
      }
    },
    async saveTimeOffPolicy() {
      let reloading = false;

      try {
        if (this.hoursWorkedSelected) {
          this.timeOffPolicy.accrual_policy.maximum_to_accrue = this.timeOffPolicy.accrual_policy.hours_per_year;
          delete this.timeOffPolicy.accrual_policy.hours_per_year;
        }

        if (this.monthlySelected && this.timeOffPolicy.accrual_policy.accrual_period === 'year') {
          this.timeOffPolicy.accrual_policy.hours_per_year = Number(this.timeOffPolicy.accrual_policy.hours);
        } else if (this.monthlySelected && this.timeOffPolicy.accrual_policy.accrual_period === 'month') {
          this.timeOffPolicy.accrual_policy.hours_per_month = Number(this.timeOffPolicy.accrual_policy.hours);
        }

        const params = this.editing ? { id: this.timeOffPolicy.id } : { organizationId: this.organizationId };
        const action = this.editing ? 'update' : 'create';
        const timeOffPolicyParams = isEmpty(this.membersData)
          ? { time_off_policy: this.timeOffPolicy, members_added_by: this.membersAddedBy }
          : {
              time_off_policy: this.timeOffPolicy,
              members_data: this.membersData,
              members_added_by: this.membersAddedBy,
            };
        const response = await TimeOffPoliciesResource[action](params, timeOffPolicyParams, { abortReload: true });

        if (response.success) {
          reloading = true;
          window.location.assign(response.url);
        } else if (response.errors) {
          this.$refs.dialogObserver.setErrors(response.errors);

          const backToTheFirstStep = TIME_OFF_POLICY_ERRORS_TO_CHECK.some((key) => key in response.errors);

          if (backToTheFirstStep) {
            this.step = 0;
          }

          this.openDialog();

          this.$appMessage(response.errors.join());
        } else {
          throw new Error('Unable to fetch data for the time off policy creation.');
        }
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.openDialog();
          this.$appMessage('Unexpected error. Please try again.');
        }
      } finally {
        if (!reloading) {
          this.saving = false;
        }
      }
    },
  },
};
